import React, { useState } from "react";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { routes } from "@common/constants/urls";
import * as deviceService from "@common/services/deviceService";
import mqttService from "@monitor/mqttService";
import mqttUtil from "@monitor/mqttUtil";
import "./status-finish-cleaning.scss";

// request.js rejects with the parsed-body PROMISE (no status code), so the body
// is the only thing a caller can branch on.
async function errorBody(err) {
  if (err && typeof err.then === "function") {
    try {
      return await err;
    } catch (e) {
      return null; // unreadable body -- treat as a generic failure
    }
  }
  return null;
}

// A base parked at the end of a (acid) CIP awaiting the teardown ack comes in two
// shapes, and they need different actions:
//
//  - WITH an ongoing cleaning session -- the normal case, since the backend's
//    teardown guards deliberately keep it ACTIVE -- route to the monitoring page.
//    UserActionManager walks the user through the real teardown there (ua:37 "CIP
//    Finished" carries a full step carousel) and then resumes the session. Acking
//    straight from this card would skip those instructions.
//
//  - WITHOUT one -- the strand: the cleaning session already went terminal, so
//    every session-scoped action is refused -- fall back to the device-scoped
//    POST /v1/devices/<uuid>/acknowledge_teardown/, which exists precisely
//    because that active-session gate is what strands these machines.
const StatusFinishCleaning = ({ deviceUUID, sessionId, dispatch }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const acknowledge = () => {
    setError(null);
    setBusy(true);
    let seq = null;
    const sub = mqttService.notification$.subscribe((n) => {
      if (n.commandSeq !== seq) return;
      if (mqttUtil.isExecuted(n)) {
        setBusy(false);
        sub.unsubscribe();
      } else if (mqttUtil.hasError(n)) {
        setBusy(false);
        setError("Machine refused");
        sub.unsubscribe();
      }
    });
    deviceService
      .acknowledgeTeardown(deviceUUID)
      .then((r) => {
        seq = r.seq;
      })
      .catch(async (err) => {
        setBusy(false);
        sub.unsubscribe();
        // The backend 409s when it does not see the device parked at CIP-done.
        // This card cannot match its process_type + process_state + user_action
        // triple (breweryoverview omits process_state), so the disagreement is
        // reachable -- and "Try again" would be a lie, since retrying cannot
        // change it.
        const body = await errorBody(err);
        const detail = (body && body.detail) || "";
        setError(
          /not awaiting/i.test(detail) ? "Machine not ready" : "Try again",
        );
      });
  };

  const onClick = () => {
    if (busy) return;
    if (sessionId != null) {
      dispatch(push(routes.monitoring + sessionId));
      return;
    }
    acknowledge();
  };

  return (
    <div onClick={onClick} className="device__status--finish-cleaning">
      <span className="ion-checkmark-circled device__alert-icon" />
      <span className="device__alert-text">
        {busy ? "Finishing cleaning..." : error ? error : "Finish cleaning"}
      </span>
      <span className="ion-ios-arrow-right device__chevron" />
    </div>
  );
};

export default connect(undefined, undefined)(StatusFinishCleaning);
